import { create } from 'zustand';
import { publicApi } from './api';
import type { CartItem, Artwork, Language, Currency, FrameOption } from '@/types';

export const cartItemKey = (item: Pick<CartItem, 'artwork' | 'frame' | 'variantId'>): string =>
  JSON.stringify([item.artwork._id, item.frame, item.variantId || '']);
interface CartState {
  items: CartItem[];
  isOpen: boolean;
  addItem: (artwork: Artwork, frame: FrameOption, quantity?: number, variantId?: string) => void;
  removeItem: (rowId: string) => void;
  updateQuantity: (rowId: string, quantity: number) => void;
  updateFrame: (rowId: string, frame: FrameOption) => void;
  clearCart: () => void;
  toggleCart: () => void;
  totalItems: () => number;
  subtotal: () => number;
  hydrateImages: () => Promise<boolean>;
}
/**
 * Frame markup for an artwork.
 *
 * The precedence must match the artwork detail page AND the server
 * (per-artwork percentage → global multiplier → none). If it drifts, the cart quotes one
 * price while checkout charges the server-computed one.
 */
function getFrameMultiplier(artwork: Artwork, frame: FrameOption): number {
  if (frame === 'unframed') return 1;
  try {
    const perArtwork = artwork.frameMarkups?.[frame as 'black' | 'white' | 'custom'];
    if (perArtwork != null && perArtwork > 0) return 1 + perArtwork / 100;

    const settings = (
      window as unknown as { __ADMIN_SETTINGS__?: { frameMultipliers?: Record<string, number> } }
    ).__ADMIN_SETTINGS__;
    const mults = settings?.frameMultipliers;
    if (mults && typeof mults[frame] === 'number') return mults[frame] || 1;
  } catch {
    /* ignore */
  }
  return 1;
}

function calculateItemPrice(
  artwork: Artwork,
  frame: FrameOption,
  quantity: number,
  variantId?: string,
): number {
  const basePrice = Math.round(
    (variantId
      ? (artwork.sizeVariants?.find((v) => v._id === variantId)?.priceCents ?? 0)
      : artwork.price.amount) * getFrameMultiplier(artwork, frame),
  );
  return basePrice * quantity;
}

function loadCartItems(): CartItem[] {
  try {
    const saved = JSON.parse(localStorage.getItem('gallery-cart-v2') || '[]');
    if (!Array.isArray(saved)) return [];
    return saved
      .filter(
        (s) => typeof s.artworkId === 'string' && Number.isInteger(s.quantity) && s.quantity > 0,
      )
      .map((s) => ({
        artwork: {
          _id: s.artworkId,
          title: s.title || { en: '', ka: '' },
          description: { en: '', ka: '' },
          images: [],
          category: s.category || '',
          medium: { en: '', ka: '' },
          year: 0,
          dimensions: s.dimensions || { width: 0, height: 0, unit: 'cm' },
          sizeClass: 'medium',
          price: { amount: s.price, currency: s.currency || 'USD' },
          frameMarkups: s.frameMarkups,
          sizeVariants: s.sizeVariants,
          rarity: 'limited',
          artist: '',
          slug: s.slug || '',
          isActive: true,
          isForSale: true,
          isFeatured: false,
          viewCount: 0,
          createdAt: '',
          updatedAt: '',
        } as Artwork,
        frame: s.frame,
        variantId: s.variantId,
        quantity: s.quantity,
        totalPrice: s.totalPrice,
      }));
  } catch {
    return [];
  }
}
function saveCartItems(items: CartItem[]): void {
  try {
    localStorage.setItem(
      'gallery-cart-v2',
      JSON.stringify(
        items.map((i) => ({
          artworkId: i.artwork._id,
          title: i.artwork.title,
          price: i.artwork.price.amount,
          currency: i.artwork.price.currency,
          frame: i.frame,
          variantId: i.variantId,
          quantity: i.quantity,
          totalPrice: i.totalPrice,
          category: i.artwork.category,
          slug: i.artwork.slug,
          frameMarkups: i.artwork.frameMarkups,
          sizeVariants: i.artwork.sizeVariants,
          dimensions: i.artwork.dimensions,
        })),
      ),
    );
  } catch {
    /* Cart remains usable in memory when storage is unavailable. */
  }
}
let refreshGeneration = 0;
export const useCartStore = create<CartState>((set, get) => {
  const update = (fn: (items: CartItem[]) => CartItem[]) =>
    set((state) => {
      const items = fn(state.items);
      saveCartItems(items);
      return { items };
    });
  return {
    items: loadCartItems(),
    isOpen: false,
    addItem: (artwork, frame, quantity = 1, variantId) =>
      update((items) => {
        const key = cartItemKey({ artwork, frame, variantId });
        const existing = items.find((i) => cartItemKey(i) === key);
        if (existing)
          return items.map((i) =>
            cartItemKey(i) === key
              ? {
                  ...i,
                  artwork,
                  quantity: i.quantity + quantity,
                  totalPrice: calculateItemPrice(artwork, frame, i.quantity + quantity, variantId),
                }
              : i,
          );
        return [
          ...items,
          {
            artwork,
            frame,
            variantId,
            quantity,
            totalPrice: calculateItemPrice(artwork, frame, quantity, variantId),
          },
        ];
      }),
    removeItem: (key) => update((items) => items.filter((i) => cartItemKey(i) !== key)),
    updateQuantity: (key, quantity) => {
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > 100) return;
      update((items) =>
        items.map((i) =>
          cartItemKey(i) === key
            ? {
                ...i,
                quantity,
                totalPrice: calculateItemPrice(i.artwork, i.frame, quantity, i.variantId),
              }
            : i,
        ),
      );
    },
    updateFrame: (key, frame) =>
      update((items) => {
        const merged = new Map<string, CartItem>();
        for (const item of items) {
          const i = cartItemKey(item) === key ? { ...item, frame } : item;
          const id = cartItemKey(i);
          const quantity = i.quantity + (merged.get(id)?.quantity || 0);
          merged.set(id, {
            ...i,
            quantity,
            totalPrice: calculateItemPrice(i.artwork, i.frame, quantity, i.variantId),
          });
        }
        return [...merged.values()];
      }),
    clearCart: () => update(() => []),
    toggleCart: () => set({ isOpen: !get().isOpen }),
    totalItems: () => get().items.reduce((n, i) => n + i.quantity, 0),
    subtotal: () => get().items.reduce((n, i) => n + i.totalPrice, 0),
    hydrateImages: async () => {
      const generation = ++refreshGeneration;
      if (!get().items.length) return false;
      const artworks = (await publicApi.getArtworksWithImages()) as Artwork[];
      if (generation !== refreshGeneration)
        throw new Error('Cart refresh superseded. Please retry.');
      const map = new Map(artworks.map((a) => [a._id, a]));
      let changed = false;
      update((items) =>
        items.map((i) => {
          const fresh = map.get(i.artwork._id);
          const unavailable =
            !fresh ||
            !fresh.isForSale ||
            (i.variantId && !fresh.sizeVariants?.some((v) => v._id === i.variantId));
          if (unavailable) {
            changed = true;
            return { ...i, unavailable: true };
          }
          const artwork = {
            ...fresh!,
            images: fresh!.images?.length ? fresh!.images : i.artwork.images,
          };
          const totalPrice = calculateItemPrice(artwork, i.frame, i.quantity, i.variantId);
          if (
            totalPrice !== i.totalPrice ||
            artwork.price.currency !== i.artwork.price.currency ||
            i.unavailable
          )
            changed = true;
          return { ...i, artwork, totalPrice, unavailable: false };
        }),
      );
      return changed;
    },
  };
});
// ============================================================================
// APP STORE (theme, language, currency)
// ============================================================================

interface AppState {
  isDarkMode: boolean;
  language: Language;
  currency: Currency;
  exchangeRate: number;
  exchangeRateDate: string;
  toggleTheme: () => void;
  setLanguage: (lang: Language) => void;
  setCurrency: (cur: Currency) => void;
  setExchangeRate: (rate: number, date?: string) => void;
}

function loadCachedRate(): number {
  try {
    const raw = localStorage.getItem('gallery-nbg-rate');
    if (raw) {
      const { rate } = JSON.parse(raw) as { rate: number };
      if (typeof rate === 'number' && rate > 0) return rate;
    }
  } catch {
    /* use fallback */
  }
  return 2.67;
}

export const useAppStore = create<AppState>((set) => ({
  isDarkMode: localStorage.getItem('gallery-theme') === 'dark',
  language: (localStorage.getItem('gallery-lang') as Language) || 'en',
  currency: (localStorage.getItem('gallery-currency') as Currency) || 'USD',
  exchangeRate: loadCachedRate(),
  exchangeRateDate: '',

  toggleTheme: () =>
    set((state) => {
      const next = !state.isDarkMode;
      localStorage.setItem('gallery-theme', next ? 'dark' : 'light');
      if (next) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      return { isDarkMode: next };
    }),

  setLanguage: (lang) => {
    localStorage.setItem('gallery-lang', lang);
    set({ language: lang });
  },

  setCurrency: (cur) => {
    localStorage.setItem('gallery-currency', cur);
    set({ currency: cur });
  },

  setExchangeRate: (rate, date) => set({ exchangeRate: rate, exchangeRateDate: date || '' }),
}));
